// CommissionScheduleSettingsWithMockApi.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Alert,
  Spinner,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import NavBar from "../../Components/NavBar";
import api from "../../../Services/Api/api";

/*
  PRESETS & helpers
*/
const PRESETS = {
  "1_16": { label: "Twice monthly — 1 & 16", dates: [1, 16], type: "dates" },
  monthly_1: { label: "Monthly — 1st", dates: [1], type: "dates" },
  every_day: { label: "Every day", dates: "all", type: "all" },
  weekdays: {
    label: "Every weekday (Mon–Fri)",
    dates: "weekdays",
    type: "dow",
  },
  custom: { label: "Custom dates", dates: [], type: "custom" },
};

function padTwo(n) {
  return String(n).padStart(2, "0");
}

/*
  MOCK API
  - getCron(): resolves to saved cron string (or null if none)
  - saveCron(cron): resolves to success
  Implementation uses localStorage to persist between reloads, and setTimeout to simulate latency.
*/
const MOCK_STORAGE_KEY = "mock_saved_cron";

function getCron() {
  return api
    .get("v1/user/self")
    .then((x) => x.user?.userSetting?.commissionSettlementCron);
}

function saveCron(cronString) {
  return api.put("v1/head-office/settings/commissionSettlementCron", {
    commissionSettlementCron: cronString,
  });
}

/*
  parseCron() - parses a standard 5-field cron string into { minute, hour, dayOfMonth, month, dayOfWeek }
  Returns null on failure.
*/
function parseCron(cronString) {
  if (!cronString || typeof cronString !== "string") return null;
  const tokens = cronString.trim().split(/\s+/);
  if (tokens.length < 5) return null;
  const [minute, hour, dom, month, dow] = tokens;
  return { minute, hour, dom, month, dow };
}

/*
  isNumericDayList: validates that dom is either '*' or comma-separated numbers 1..31
*/
function parseDomToDates(dom) {
  if (!dom) return null;
  if (dom === "*") return []; // means every day
  // only allow simple lists like "1,16" or single number "15"
  if (/^\d+(,\d+)*$/.test(dom)) {
    const nums = dom
      .split(",")
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isInteger(n) && n >= 1 && n <= 31);
    if (nums.length === 0) return null;
    return nums;
  }
  return null;
}

/*
  buildCronFromState - returns cron string from minute, hour, preset/meta
  We only support cron patterns that correspond to UI:
    - every day:  M H * * *
    - weekdays:   M H * * 1-5
    - dates list: M H D,D,D * *
*/
function buildCron(minute, hour, presetType, selectedDates) {
  const M = Number(minute);
  const H = Number(hour);
  if (presetType === "every_day") return `${M} ${H} * * *`;
  if (presetType === "weekdays") return `${M} ${H} * * 1-5`;
  // dates (custom or specific preset)
  if (!selectedDates || selectedDates.length === 0) return `${M} ${H} * * *`; // fallback
  const days = selectedDates.join(",");
  return `${M} ${H} ${days} * *`;
}

/*
  Component
*/
export default function CommissionScheduleSettingsWithMockApi() {
  // UI state
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [cronRaw, setCronRaw] = useState(""); // raw cron fetched (read-only display)
  const [preset, setPreset] = useState("1_16");
  const [selectedDates, setSelectedDates] = useState([1, 16]); // array of numbers 1..31
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);

  const [showHelp, setShowHelp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);

  // on mount: fetch cron string and parse into UI state
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getCron()
      .then((cronStr) => {
        if (!mounted) return;
        setCronRaw(cronStr);
        const parsed = parseCron(cronStr);
        if (!parsed) {
          // fallback default
          applyDefaultState();
          setLoading(false);
          return;
        }
        // try interpret parsed
        const { minute: mTok, hour: hTok, dom, month, dow } = parsed;

        // Only accept basic month wildcard ('*') — if month is not '*' we will fallback to default
        if (month !== "*") {
          applyDefaultState();
          setLoading(false);
          return;
        }

        // validate numeric minute/hour tokens (we'll coerce to numbers if possible)
        const mNum = /^\d+$/.test(mTok) ? Number(mTok) : null;
        const hNum = /^\d+$/.test(hTok) ? Number(hTok) : null;
        if (
          mNum === null ||
          hNum === null ||
          mNum < 0 ||
          mNum > 59 ||
          hNum < 0 ||
          hNum > 23
        ) {
          applyDefaultState();
          setLoading(false);
          return;
        }

        // interpret day-of-week patterns first (e.g., '1-5' => weekdays)
        if (dow === "1-5" && dom === "*") {
          setMinute(mNum);
          setHour(hNum);
          setPreset("weekdays");
          setSelectedDates([]); // empty signals not dates
          setLoading(false);
          return;
        }

        // if dom is '*' => every day
        if (dom === "*") {
          setMinute(mNum);
          setHour(hNum);
          setPreset("every_day");
          setSelectedDates([]);
          setLoading(false);
          return;
        }

        // if dom is a simple list of numbers e.g., "1,16" or "15"
        const domDates = parseDomToDates(dom);
        if (domDates) {
          setMinute(mNum);
          setHour(hNum);
          // map to known presets if matches exactly
          const domStr = domDates.join(",");
          if (domStr === "1,16") {
            setPreset("1_16");
          } else if (domStr === "1") {
            setPreset("monthly_1");
          } else {
            setPreset("custom");
          }
          setSelectedDates(domDates);
          setLoading(false);
          return;
        }

        // anything else (complex expressions) -> fallback default
        applyDefaultState();
        setLoading(false);
      })
      .catch((err) => {
        console.error("fetch cron failed", err);
        if (!mounted) return;
        setFetchError("Failed to load saved schedule.");
        applyDefaultState();
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyDefaultState() {
    // default to 1 & 16 midnight
    setPreset("1_16");
    setSelectedDates([1, 16]);
    setHour(0);
    setMinute(0);
    setCronRaw("0 0 1,16 * *");
  }

  // toggle date pill
  function toggleDate(d) {
    setPreset("custom");
    setSelectedDates((prev) => {
      if (prev.includes(d)) return prev.filter((x) => x !== d);
      return [...prev, d].sort((a, b) => a - b);
    });
  }

  // build cron preview & human readable from current state
  const { cronPreview, readable } = useMemo(() => {
    const pType =
      preset === "weekdays"
        ? "weekdays"
        : preset === "every_day"
        ? "every_day"
        : "dates";
    const cron = buildCron(
      minute,
      hour,
      pType === "weekdays"
        ? "weekdays"
        : pType === "every_day"
        ? "every_day"
        : "dates",
      selectedDates
    );
    const readText =
      pType === "weekdays"
        ? `Runs every weekday (Mon–Fri) at ${padTwo(hour)}:${padTwo(minute)}.`
        : pType === "every_day"
        ? `Runs every day at ${padTwo(hour)}:${padTwo(minute)}.`
        : selectedDates && selectedDates.length > 0
        ? `Runs on day(s) ${selectedDates.join(",")} of each month at ${padTwo(
            hour
          )}:${padTwo(minute)}.`
        : `Runs every day at ${padTwo(hour)}:${padTwo(minute)}.`; // fallback
    return { cronPreview: cron, readable: readText };
  }, [preset, selectedDates, hour, minute]);

  // copy cronPreview to clipboard
  const copyCron = async () => {
    try {
      await navigator.clipboard.writeText(cronPreview);
      setInfo({ variant: "success", text: "Cron string copied to clipboard." });
      setTimeout(() => setInfo(null), 1800);
    } catch (err) {
      setInfo({
        variant: "warning",
        text: "Copy failed — please copy manually.",
      });
      setTimeout(() => setInfo(null), 2500);
    }
  };

  const handleSave = async () => {
    // build cron from UI state and send only that to mock API
    const sendCron = cronPreview;
    setSaving(true);
    setInfo(null);
    try {
      await saveCron(sendCron);
      setCronRaw(sendCron);
      setInfo({ variant: "success", text: "Schedule saved." });
    } catch (err) {
      console.error("save failed", err);
      setInfo({ variant: "danger", text: "Failed to save schedule." });
    } finally {
      setSaving(false);
      setTimeout(() => setInfo(null), 2200);
    }
  };

  // UI date buttons 1..31
  const dateButtons = [];
  for (let d = 1; d <= 31; d++) {
    const selected = selectedDates.includes(d);
    dateButtons.push(
      <Button
        key={d}
        variant={selected ? "primary" : "outline-secondary"}
        size="sm"
        className="me-1 mb-1"
        onClick={() => toggleDate(d)}
        aria-pressed={selected}
        aria-label={`Day ${d}`}
      >
        {d}
      </Button>
    );
  }

  // small helper to set preset and adjust selectedDates
  function applyPreset(pKey) {
    const p = PRESETS[pKey];
    setPreset(pKey);
    if (p.type === "dates" && Array.isArray(p.dates)) {
      setSelectedDates(p.dates.slice());
    } else {
      setSelectedDates([]); // special meaning handled when building cron
    }
  }

  return (
    <>
      <NavBar activeLink="/setting" />
      <Container className="gainner">
        <Card>
          <Card.Body>
            <Card.Title>Automated Commission Settlement</Card.Title>
            <Card.Text>
              Choose when automatic commission settlements should run. The cron
              string is generated for you and is read-only.
            </Card.Text>

            {loading ? (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                <div>Loading saved schedule...</div>
              </div>
            ) : (
              <>
                {fetchError && <Alert variant="danger">{fetchError}</Alert>}

                <Form>
                  <Row className="align-items-center mb-3">
                    <Col md={4}>
                      <Form.Group controlId="presetSelect">
                        <Form.Label>Preset</Form.Label>
                        <Form.Select
                          value={preset}
                          onChange={(e) => applyPreset(e.target.value)}
                        >
                          {Object.entries(PRESETS).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Time (24-hour)</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            min="0"
                            max="23"
                            value={hour}
                            onChange={(e) =>
                              setHour(
                                Math.min(
                                  23,
                                  Math.max(0, Number(e.target.value || 0))
                                )
                              )
                            }
                            aria-label="Hour"
                          />
                          <InputGroup.Text>:</InputGroup.Text>
                          <Form.Control
                            type="number"
                            min="0"
                            max="59"
                            value={minute}
                            onChange={(e) =>
                              setMinute(
                                Math.min(
                                  59,
                                  Math.max(0, Number(e.target.value || 0))
                                )
                              )
                            }
                            aria-label="Minute"
                          />
                          <InputGroup.Text className="ms-2">
                            <strong>
                              {padTwo(hour)}:{padTwo(minute)}
                            </strong>
                          </InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <Form.Label>
                        Dates{" "}
                        <small className="text-muted">(pick multiple)</small>
                      </Form.Label>
                      <div className="mb-2">{dateButtons}</div>

                      <Form.Check
                        type="checkbox"
                        id="allDaysCheckbox"
                        label="All days (run every day)"
                        checked={
                          preset === "every_day" ||
                          (preset !== "custom" &&
                            PRESETS[preset].type === "all")
                        }
                        onChange={(e) => {
                          if (e.target.checked) applyPreset("every_day");
                          else applyPreset("custom");
                        }}
                      />
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={8}>
                      <Form.Label>Preview</Form.Label>
                      <Card className="p-2">
                        <div>{readable}</div>
                      </Card>
                    </Col>

                    <Col md={4}>
                      <Form.Label>
                        Cron string{" "}
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="cron-tip">
                              Generated from your selections — read-only.
                            </Tooltip>
                          }
                        >
                          <sup style={{ cursor: "pointer" }}>
                            <Badge bg="secondary">?</Badge>
                          </sup>
                        </OverlayTrigger>
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          value={cronPreview}
                          readOnly
                          aria-readonly
                        />
                        <Button variant="outline-secondary" onClick={copyCron}>
                          Copy
                        </Button>
                      </InputGroup>
                      <div className="small text-muted mt-2">
                        Format: minute hour day-of-month month day-of-week
                      </div>
                      <div className="small text-muted mt-1">
                        Saved cron: <code>{cronRaw}</code>
                      </div>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col>
                      <Button
                        variant="primary"
                        onClick={handleSave}
                        className="me-2"
                        disabled={saving}
                      >
                        {saving ? <>Saving…</> : "Save"}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          // cancel: reload saved cron from mock API
                          setLoading(true);
                          getCron().then((c) => {
                            setCronRaw(c);
                            const parsed = parseCron(c);
                            if (!parsed) {
                              applyDefaultState();
                              setLoading(false);
                              return;
                            }
                            const {
                              minute: mTok,
                              hour: hTok,
                              dom,
                              month,
                              dow,
                            } = parsed;
                            // validate and reapply logic similar to load effect
                            if (month !== "*") {
                              applyDefaultState();
                              setLoading(false);
                              return;
                            }
                            const mNum = /^\d+$/.test(mTok)
                              ? Number(mTok)
                              : null;
                            const hNum = /^\d+$/.test(hTok)
                              ? Number(hTok)
                              : null;
                            if (mNum === null || hNum === null) {
                              applyDefaultState();
                              setLoading(false);
                              return;
                            }
                            if (dow === "1-5" && dom === "*") {
                              setMinute(mNum);
                              setHour(hNum);
                              setPreset("weekdays");
                              setSelectedDates([]);
                              setLoading(false);
                              return;
                            }
                            if (dom === "*") {
                              setMinute(mNum);
                              setHour(hNum);
                              setPreset("every_day");
                              setSelectedDates([]);
                              setLoading(false);
                              return;
                            }
                            const domDates = parseDomToDates(dom);
                            if (domDates) {
                              setMinute(mNum);
                              setHour(hNum);
                              const domStr = domDates.join(",");
                              if (domStr === "1,16") setPreset("1_16");
                              else if (domStr === "1") setPreset("monthly_1");
                              else setPreset("custom");
                              setSelectedDates(domDates);
                              setLoading(false);
                              return;
                            }
                            applyDefaultState();
                            setLoading(false);
                          });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="link"
                        onClick={() => setShowHelp(true)}
                        className="ms-3"
                      >
                        What is a cron?
                      </Button>
                    </Col>
                  </Row>
                </Form>

                {info && (
                  <Alert variant={info.variant} className="mt-3">
                    {info.text}
                  </Alert>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        <Modal show={showHelp} onHide={() => setShowHelp(false)}>
          <Modal.Header closeButton>
            <Modal.Title>What is a cron string?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              A cron string tells the scheduler when to run the job. The format
              used here is:{" "}
              <code>minute hour day-of-month month day-of-week</code>.
            </p>
            <ul>
              <li>
                <code>0 0 1,16 * *</code> = runs at 00:00 on the 1st and 16th of
                every month.
              </li>
              <li>
                <code>0 6 * * 1-5</code> = runs at 06:00 every weekday (Monday
                to Friday).
              </li>
              <li>
                We only support standard cron patterns that map to the UI.
                Complex cron expressions (steps, ranges in day-of-month,
                non-star months) will fall back to the default schedule.
              </li>
            </ul>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowHelp(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
}
